import { FormBaseV2, ResourceBaseV2 } from "src/app/common/models/model-base";
import { WfSignature, WfTaskLocalResource } from "../workflow/workflow";
import { Resource } from "../catalogs/beamline-catalog/resource/resources";
import { Restriction } from "src/app/controls/restrictions/services/restriction";
import { Role } from "../catalogs/roles/services/role";
import { RelatedDocument } from "src/app/services/documents/documents";
import { RelatedLinkBase } from "../catalogs/navigation-links/link/related-link-base";
import { FormRestriction } from "src/app/services/forms/form-restriction";
import { FormStatusEnum, FormType, InsertOrigin } from "src/app/common/enumerations/enumerations";

export interface RSFBase extends FormBaseV2 {

  shielding: boolean;
  shieldingRemoveReplace: boolean;
  shieldingModification: boolean;
  shieldingModificationARC: boolean;
  shieldingModificationBRC: boolean;

  pps: boolean;
  ppsMaintenance: boolean;
  ppsModification: boolean;
  ppsModificationARC: boolean;
  ppsModificationBRC: boolean;
  ppsAcis: boolean;
  tombis: boolean;
  ppsOther: boolean;
  ppsOtherText: string;

  radMonitors: boolean;
  radMonitorsMoving: boolean;
  radMonitorsDisconnecting: boolean;
  radMonitorsModifying: boolean;
  radMonitorsTripPoint: boolean;

  radSurveyRequired: boolean;

  ppsplcSet: boolean;
  ppsSoftware: boolean;
  etiAccess: boolean;
  tombisplcSet: boolean;
  tombisSoftware: boolean;
  topOffInterlockSetPoint: boolean;

  beamLine: boolean;
  accelerator: boolean;
  otherSystems: boolean;

  rsfRelatedDocuments: RSFRelatedDocument[];
  rsfRelatedLinks: RSFRelatedLink[];
  rsfHoldPoints: RSFHoldPoint[];
  rsfResourceRelations: RSFResourceRelation[];
  rsfRestrictions: RSFRestriction[];
  rsfRoleNotifications: RSFRoleNotification[];
  rsfOtherSystems: RSFOtherSystem[];

  functionalTests: boolean;

  kind?: Kind;
  safetyControlsChanged?: boolean;
  rfSystemLocation?: boolean;
  workerVerificationSigned?: boolean;

  // Internal
  locationNames?: string;
  amendmentCount?: number;
}

export interface RSF extends RSFBase {
  rsfAmendments: RSFAmendment[];
  rsf: RSF;
}


export interface RSFHoldPoint extends ResourceBaseV2 {
  // Base properties
  rsfid?: number;
  rsf?: RSF;

  // Amendnment properties
  rsfAmendmentID?: number;
  rsfAmendment?: RSFAmendment;

  // Common Properties
  number: number;
  wfTaskLocals?: WfTaskLocalResource[];
  signatures?: WfSignature[];
}

export interface RSFResourceRelation extends ResourceBaseV2 {
  // Base properties
  rsfID?: number;
  rsf?: RSF;

  // Amendnment properties
  rsfAmendmentID?: number;
  rsfAmendment?: RSFAmendment;

  // Common Properties
  resourceID: number;
  resource: Resource;
  insertOrigin?: InsertOrigin;
}

export interface RSFRestriction extends ResourceBaseV2 {
  // Base properties
  rsfID?: number;
  rsf?: RSF;

  // Amendnment properties
  rsfAmendmentID?: number;
  rsfAmendment?: RSFAmendment;

  // Common Properties
  restrictionID?: number | null;
  restriction: Restriction;
  restrictionText: string;

  // Internal
  hilite?: boolean | null;
  deleted?: boolean | null;
}

export interface RSFRoleNotification extends ResourceBaseV2 {
  // Base properties
  rsfID?: number;
  rsf?: RSF;

  // Amendnment properties
  rsfAmendmentID?: number;
  rsfAmendment?: RSFAmendment;

  // Common Properties
  roleID?: number;
  role?: Role;
  checked?: boolean;

  // Internal
  changed?: boolean;
}

export interface RSFRelatedLink extends RelatedLinkBase {
  // Base properties
  rsfID?: number;
  rsf?: RSF;

  // Amendnment properties
  rsfAmendmentID?: number;
  rsfAmendment?: RSFAmendment;
}

export interface RSFOtherSystem extends ResourceBaseV2 {
  // Base properties
  rsfID?: number;
  rsf?: RSF;

  // Amendnment properties
  rsfAmendmentID?: number;
  rsfAmendment?: RSFAmendment;
}

export interface RSFRelatedDocument extends ResourceBaseV2 {
  // Base properties
  rsfID?: number;
  rsf?: RSF;

  // Amendnment properties
  rsfAmendmentID?: number;
  rsfAmendment?: RSFAmendment;

  // Common properties
  relatedDocumentID: number;
  relatedDocument: RelatedDocument;
  relatedDocumentType: FormType;
  type: RSFRelatedDocumentType;
}

export interface RSFAmendment extends RSFBase {
  rsfID: number;
  rsf: RSF;
  amendmentNumber: number;
  active?: boolean;
  number?: number;
  wfAmendmentID?: number;
  isMinorAmendment?: boolean;
}


export enum RSFRelatedDocumentType {
  ASCCForm = 0,
  ParentForm = 1,
  ChildForm = 2
}

export enum RSFResourceRelationType {
  Location = 0,
  Beamline = 1,
  Accelerator = 2
}

export class RSFRestrictionMapper {
  static fromForm(r: FormRestriction, rsf: RSF, rsfAmendment?: RSFAmendment | null): RSFRestriction {
    return {
      id: r.id ?? 0,
      rsfID: rsf.id,
      rsfAmendmentID: rsfAmendment?.id,
      restrictionID: r.restrictionID,
      restriction: r.restriction!,
      restrictionText: r.text ?? '',
      status: 1,
      hilite: r.hilite,
      deleted: r.deleted
    } as RSFRestriction;
  }

  static fromForms(r: FormRestriction[], rsf: RSF, rsfAmendment?: RSFAmendment | null): RSFRestriction[] {
    return r.map(r => this.fromForm(r, rsf, rsfAmendment));
  }

  static fromRSF(rsfRestrictions?: RSFRestriction[], oRestrictions?: RSFRestriction[]): FormRestriction[] {
    const formRestrictions: FormRestriction[] = [];
    rsfRestrictions?.forEach(rsfRestriction => {
      const formRestriction = {
        id: rsfRestriction.id,
        formID: rsfRestriction.rsfID,
        restrictionID: rsfRestriction.restrictionID ?? undefined,
        restriction: rsfRestriction.restriction,
        text: rsfRestriction.restrictionText,
        hilite: rsfRestriction.hilite,
        deleted: rsfRestriction.deleted
      } as FormRestriction;

      const existsInOriginal = oRestrictions?.find(r => r.restrictionID == rsfRestriction.restrictionID || (r.restrictionText && r.restrictionText == rsfRestriction.restrictionText));
      formRestriction.hilite = !existsInOriginal;

      formRestrictions.push(formRestriction);
    });

    oRestrictions?.forEach(rsfRestriction => {
      const existsInUpdated = rsfRestrictions?.find(r => r.restrictionID == rsfRestriction.restrictionID || (r.restrictionText && r.restrictionText == rsfRestriction.restrictionText));

      if (!existsInUpdated) {
        const formRestriction = {
          id: rsfRestriction.id,
          formID: rsfRestriction.rsfID,
          restrictionID: rsfRestriction.restrictionID ?? undefined,
          restriction: rsfRestriction.restriction,
          text: rsfRestriction.restrictionText,
          hilite: rsfRestriction.hilite,
          deleted: true
        } as FormRestriction;
        formRestrictions.push(formRestriction);
      }
    });

    return formRestrictions;
  }
}

export class RSFStatusResource {
  id!: number;
  status!: FormStatusEnum;
}

export enum RSFButtonAction {
  // RSF
  Create,
  Edit,
  Delete,
  SaveDraft,
  Submit,
  Unsubmit,
  Disapprove,
  Cancel,
  Duplicate,
  Discard,

  // Amendments
  CreateAmendment,
  CreateMinorAmendment,
  EditAmendment,
  SaveAmendment,
  SubmitAmendment,
  UnsubmitAmendment,
  DisapproveAmendment,
  CancelAmendment,

  // Common
  Release,
}

export enum Kind {
  RSF,
  RSFAmendment
}
